import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  styled,
  InputAdornment,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import {
  Send,
  EmojiEmotions,
  Gif,
  ChatBubbleOutline,
  Poll,
} from "@mui/icons-material";
import {
  useGetMessages,
  useSendMessage,
} from "../../services/stagemode_api/viewers/viewers_api";
import { Icons } from "../../components/assets/icon";
import SocketHandler from "../../utils/socketHandler";
import {
  useGetGuestUsers,
} from "../../services/stagemode_api/moderator/moderator_api";
// Styled components for the chat
const ChatContainer = styled(Paper)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 14,
  background: "radial-gradient(circle at top, #1f2937 0, #020617 55%, #000 100%)",
  border: "1px solid rgba(148,163,184,0.4)",
  boxShadow:
    "0 18px 45px rgba(15,23,42,0.9), 0 0 0 1px rgba(15,23,42,0.9), inset 0 0 0 1px rgba(15,23,42,0.75)",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column-reverse",
  gap: 10,
  padding: "14px 14px 10px",
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#333",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#555",
    borderRadius: "2px",
    "&:hover": {
      background: "#777",
    },
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isHost",
})<{ isHost?: boolean }>(({ isHost }) => ({
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  maxWidth: "95%",
  alignSelf: isHost ? "flex-end" : "flex-start",
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  padding: "8px 12px 9px",
  marginBottom: 2,
  borderRadius: 12,
  background:
    "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.85)), radial-gradient(circle at top left, rgba(96,165,250,0.35), transparent 55%)",
  border: "1px solid rgba(148,163,184,0.4)",
  position: "relative",
  fontSize: "0.875rem",
}));

const MessageContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "4px",
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: "10px 12px 12px",
  borderTop: "1px solid rgba(30,64,175,0.5)",
  background: "linear-gradient(to top, rgba(15,23,42,0.98), rgba(15,23,42,0.9))",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 999,
    backgroundColor: "rgba(15,23,42,0.95)",
    color: "#fff",
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "rgba(51,65,85,0.9)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(148,163,184,0.9)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(59,130,246,0.9)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#e5e7eb",
    "&::placeholder": {
      color: "#6b7280",
    },
  },
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.35)",
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
    color: "#e5e7eb",
  },
  "& .MuiTypography-root": {
    color: "#e5e7eb",
    fontWeight: 700,
    fontSize: "0.9rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
}));

// Props interface
interface LiveChatProps {
  bookingId: string;
  username: string;
  onHighlightQuestion?: (questionId: string) => void;
  height?: string | number;
}

const LiveChat: React.FC<LiveChatProps> = ({
  bookingId,
  username,
  onHighlightQuestion,
  height = "100%",
}) => {
  const [newMessage, setNewMessage] = useState("");
  const [highlightedQuestion, setHighlightedQuestion] = useState<string | null>(
    null
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const socketSubscribedRef = useRef(false);
    const {
      data: guestUsers,
      isLoading: guestUserLoading,
      refetch: refetchGuestUsers,
    } = useGetGuestUsers({
      params: { id: bookingId ?? "" },
      key: ["guest-users", bookingId],
    });

  const {
    data: messagesData,
    isLoading,
    refetch: refetchMessages,
  } = useGetMessages(bookingId ?? "");
  const sendMessageMutation = useSendMessage(bookingId ?? "");

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messagesData]);

  useEffect(() => {
    if (socketSubscribedRef.current || !bookingId) {
      return;
    }

    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToBooking(bookingId, (message) => {
      console.log("message", message);
      refetchMessages();
    });

    socketSubscribedRef.current = true;

    return () => {
      socketHandler.unsubscribeFromBooking(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchMessages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await sendMessageMutation.mutateAsync({
        message: newMessage,
        username: username,
      });
      setNewMessage("");
      refetchMessages();
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  if (!bookingId) {
    return (
      <Box
        sx={{
          width: "100%",
          height: height,
          background: "#232326",
          borderRadius: 2,
          p: 2,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #333",
        }}
      >
        <Typography>No booking ID available</Typography>
      </Box>
    );
  }

  // Map backend reaction labels to actual emoji shown in chat
  const REACTION_EMOJI: Record<string, string> = {
    Like: "👍",
    Heart: "❤️",
    Clap: "👏",
    Star: "⭐",
  };

  return (
    <ChatContainer sx={{ height }}>
      <TitleContainer>
        <ChatBubbleOutline />
        <Typography>Live Chat</Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label="Realtime"
          sx={{
            height: 20,
            borderRadius: 999,
            fontSize: "0.65rem",
            fontWeight: 600,
            background:
              "linear-gradient(90deg, rgba(96,165,250,0.25), rgba(56,189,248,0.25))",
            border: "1px solid rgba(129,140,248,0.7)",
            color: "#e5e7eb",
          }}
        />
      </TitleContainer>

      <MessagesContainer ref={messagesContainerRef}>
        {isLoading ? (
          <Typography
            align="center"
            sx={{ color: "#999", fontSize: "0.875rem" }}
          >
            Loading messages...
          </Typography>
        ) : Array.isArray(messagesData?.data) ? (
          messagesData?.data?.map((message: any) => (
            <MessageBubble
              key={message.id}
              isHost={message.username === username}
            >
              {message.type === "Text" && (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: message.username === username ? "#666" : "#555",
                    fontSize: "0.75rem",
                  }}
                >
                  {message.username[0]}
                </Avatar>
              )}
              <Box sx={{ position: "relative" }}>
                {message.type === "Text" && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      ml: 0.5,
                      fontWeight: 500,
                      display: "block",
                      mb: 0.5,
                      fontSize: "0.72rem",
                    }}
                  >
                    {message.username}
                  </Typography>
                )}
                <MessageContainer>
                  <MessageHeader>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#e5e7eb",
                        fontSize: "0.75rem",
                      }}
                    >
                      {message.username}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#9ca3af",
                        fontSize: "0.7rem",
                      }}
                    >
                      {message.created_at}
                    </Typography>
                  </MessageHeader>
                  <MessageContent>
                    {message.type === "Tip" && (
                      <>
                        <Typography
                          sx={{ color: "#e5e7eb", fontSize: "0.86rem" }}
                        >
                          {message.message}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                            p: 0.5,
                            background: alpha("#f97316", 0.18),
                            borderRadius: "6px",
                          }}
                        >
                          <Icons.Coin />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: "bold",
                              color: "#fefce8",
                              fontSize: "0.75rem",
                            }}
                          >
                            {message.amount} {message.currency}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label="Tip"
                          color="warning"
                          icon={<Icons.Coin />}
                          sx={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            height: "18px",
                            "& .MuiChip-label": {
                              fontSize: "0.65rem",
                              fontWeight: 600,
                            },
                          }}
                        />
                      </>
                    )}
                    {(message.type === "PollVote" ||
                      message.type === "Poll") && (
                      <>
                        <Typography
                          sx={{ color: "#e5e7eb", fontSize: "0.86rem" }}
                        >
                          {message.message}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                            p: 0.5,
                            background: alpha("#2196f3", 0.2),
                            borderRadius: "6px",
                          }}
                        >
                          {message.type === "PollVote" && (
                            <>
                              <Poll
                                sx={{ color: "#2196f3", fontSize: "1rem" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "#fff",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Voted for: {message.selected_choice}
                              </Typography>
                            </>
                          )}
                          {message.type === "Poll" && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              {message.choices.map((choice: any) => (
                                <Box
                                  key={choice}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Poll
                                    sx={{
                                      color: "#2196f3",
                                      fontSize: "0.875rem",
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "#fff",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {choice}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Chip
                          size="small"
                          label="Poll"
                          color="info"
                          icon={<Poll />}
                          sx={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            backgroundColor: "#2196f3",
                            height: "18px",
                            "& .MuiChip-label": {
                              fontSize: "0.65rem",
                              fontWeight: 600,
                            },
                          }}
                        />
                      </>
                    )}
                    {message.type === "Text" && (
                      <Typography sx={{ color: "#e5e7eb", fontSize: "0.86rem" }}>
                        {message.message}
                      </Typography>
                    )}
                    {message.type === "Reaction" && (
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "0.875rem",
                        }}
                      >
                        {String(message.message || "").replace(
                          /\b(Heart|Like|Clap|Star)\b/g,
                          (m) => REACTION_EMOJI[m] ?? m,
                        )}
                      </Typography>
                    )}
                    {message.type === "PollClosed" && (
                      <Typography sx={{ color: "#fff", fontSize: "0.875rem" }}>
                        {message.message}
                      </Typography>
                    )}
                  </MessageContent>
                </MessageContainer>
              </Box>
            </MessageBubble>
          ))
        ) : (
          <Typography
            align="center"
            sx={{ color: "#999", fontSize: "0.875rem" }}
          >
            No messages available
          </Typography>
        )}
      </MessagesContainer>

      <form onSubmit={handleSendMessage}>
        <InputContainer>
          <StyledTextField
            fullWidth
            variant="outlined"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    sx={{
                      color: "#999",
                      "&:hover": { color: "#fff" },
                      transition: "color 0.2s ease",
                      padding: "4px",
                    }}
                    size="small"
                    disabled={sendMessageMutation.isLoading}
                  >
                    <EmojiEmotions fontSize="small" />
                  </IconButton>
                  <IconButton
                    sx={{
                      color: "#999",
                      "&:hover": { color: "#fff" },
                      transition: "color 0.2s ease",
                      padding: "4px",
                    }}
                    size="small"
                    disabled={sendMessageMutation.isLoading}
                  >
                    <Gif fontSize="small" />
                  </IconButton>
                  <IconButton
                    type="submit"
                    sx={{
                      color: "#2196f3",
                      "&:hover": {
                        backgroundColor: alpha("#2196f3", 0.1),
                      },
                      transition: "all 0.2s ease",
                      padding: "4px",
                    }}
                    size="small"
                    disabled={sendMessageMutation.isLoading}
                  >
                    <Send fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </InputContainer>
      </form>
    </ChatContainer>
  );
};

export default LiveChat;
